import React, { useState } from 'react';
import {
  MessageSquare, Globe, Instagram, ArrowLeft, ShoppingBag, MapPin,
  Calendar, ExternalLink, Eye, Star, Share2, Phone, ChevronRight,
  CheckCircle2, Youtube, LayoutGrid, Link as LinkIcon, Settings, Sliders, Heart
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Prospect } from '../../shared/types';

interface BiolinkDemoProps {
  prospect: Prospect;
}

export const BiolinkDemo: React.FC<BiolinkDemoProps> = ({ prospect }) => {
  const [activeTab, setActiveTab] = useState<'links' | 'loja' | 'agenda'>('links');

  const targetProspect = prospect || {
    trade_name: 'Bruta Burger & Craft',
    segment: 'Fast Food · Delivery Premium',
    whatsapp: '554988416296',
    city: 'Chapecó',
    state: 'SC',
    instagram: 'https://instagram.com/youmachine_br',
    slug: 'bruta-burger'
  };

  const whatsappUrl = `https://wa.me/${targetProspect.whatsapp}?text=${encodeURIComponent(
    `Olá! Vi o Linkwi da ${targetProspect.trade_name} e gostaria de fazer um pedido.`
  )}`;

  // Stable Unsplash product images
  const CATALOG = [
    {
      name: 'Bruto Bacon Triple', price: 'R$ 38,90', tag: '🔥 Mais Pedido',
      img: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=200&q=80&auto=format&fit=crop',
    },
    {
      name: 'Smash Double Cheese', price: 'R$ 29,90', tag: '⭐ Novo',
      img: 'https://images.unsplash.com/photo-1586190848861-99aa4a171e90?w=200&q=80&auto=format&fit=crop',
    },
    {
      name: 'Combo Família', price: 'R$ 89,90', tag: '🎉 25% OFF',
      img: 'https://images.unsplash.com/photo-1550547660-d9450f859349?w=200&q=80&auto=format&fit=crop',
    },
  ];

  const SCHEDULE = [
    { day: 'Seg–Sex', time: '11h – 23h', open: true },
    { day: 'Sábado',  time: '11h – 00h', open: true },
    { day: 'Domingo', time: '12h – 22h', open: true },
  ];

  // Avatar/cover photo
  const COVER_URL  = 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600&q=80&auto=format&fit=crop';
  const AVATAR_URL = 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=200&q=80&auto=format&fit=crop';

  return (
    <div className="min-h-screen bg-[#F0F2F5] flex flex-col items-center font-sans selection:bg-[#0061FF] selection:text-white">

      {/* ── EDITOR SIDEBAR (md+) ── */}
      <div className="hidden md:flex fixed left-0 top-0 h-full w-14 bg-white border-r border-slate-100 flex-col items-center py-6 gap-5 z-40">
        <div className="w-8 h-8 rounded-xl bg-[#0061FF] text-white font-black text-xs flex items-center justify-center shadow-sm">L</div>
        {[
          { icon: Sliders, label: 'Links' },
          { icon: LayoutGrid, label: 'Layout' },
          { icon: LinkIcon, label: 'Redes' },
          { icon: Settings, label: 'Config' },
        ].map(item => (
          <button key={item.label} className="flex flex-col items-center gap-1 text-slate-400 hover:text-[#0061FF] transition-colors">
            <item.icon className="w-4 h-4" />
            <span className="text-[8px] font-bold">{item.label}</span>
          </button>
        ))}
      </div>

      {/* ── TOP BAR ── */}
      <div className="w-full bg-white border-b border-slate-100 px-4 py-2.5 flex items-center justify-between z-30 md:pl-20">
        <div className="flex items-center gap-3">
          <Link to={`/${targetProspect.slug || 'bruta-burger'}`} className="text-slate-400 hover:text-[#0061FF] transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="bg-slate-100 border border-slate-200 rounded-lg px-3 py-1 text-[11px] font-mono text-slate-600 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            linkwi.com.br/{targetProspect.slug || 'suamarca'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-slate-500 hidden sm:flex items-center gap-1">
            <Eye className="w-3 h-3 text-[#0061FF]" /> 1.842 views hoje
          </span>
          <button className="bg-[#0061FF] text-white text-[10px] font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 shadow-sm">
            <Share2 className="w-3 h-3" /> Compartilhar
          </button>
        </div>
      </div>

      {/* ── PHONE PREVIEW ── */}
      <div className="flex-1 flex items-start justify-center py-8 px-4 md:pl-20">
        <div className="w-full max-w-[380px]">

          <div className="bg-white rounded-[40px] border border-slate-200 overflow-hidden" style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.09)' }}>

            {/* Notch */}
            <div className="flex justify-center pt-3 pb-0">
              <div className="w-20 h-5 bg-slate-900 rounded-full" />
            </div>

            {/* ── COVER PHOTO WITH PROFILE ── */}
            <div className="relative">
              {/* Cover image */}
              <div className="h-32 overflow-hidden relative">
                <img
                  src={COVER_URL}
                  className="w-full h-full object-cover"
                  alt="Cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/40" />
              </div>

              {/* Avatar overlapping cover */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2">
                <div className="w-16 h-16 rounded-full border-[3px] border-white overflow-hidden shadow-md">
                  <img src={AVATAR_URL} className="w-full h-full object-cover" alt="Avatar" />
                </div>
                <div className="absolute bottom-0 right-0 w-5 h-5 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                  <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={3} />
                </div>
              </div>
            </div>

            {/* ── PROFILE INFO ── */}
            <div className="px-5 pt-12 pb-4 text-center space-y-1 border-b border-slate-100">
              <h1 className="text-base font-black text-slate-900 flex items-center justify-center gap-1">
                {targetProspect.trade_name}
                <span className="text-[#0061FF] text-xs">✓</span>
              </h1>
              <p className="text-[11px] text-slate-500 font-medium">{targetProspect.segment}</p>
              <div className="flex items-center justify-center gap-3 mt-1">
                <span className="text-[10px] text-slate-500 flex items-center gap-0.5">
                  <MapPin className="w-3 h-3 text-rose-500" /> {targetProspect.city || 'Chapecó'}, {targetProspect.state || 'SC'}
                </span>
                <span className="text-[10px] font-bold text-amber-600 flex items-center gap-0.5">
                  <Star className="w-3 h-3 fill-amber-400" /> 4.9 · 2.4k avaliações
                </span>
              </div>

              {/* Social row */}
              <div className="flex items-center justify-center gap-3 pt-2">
                {[
                  { icon: Instagram, color: 'text-pink-500', bg: 'bg-pink-50' },
                  { icon: Youtube, color: 'text-rose-600', bg: 'bg-rose-50' },
                  { icon: Globe, color: 'text-blue-600', bg: 'bg-blue-50' },
                  { icon: Phone, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                ].map((s, i) => (
                  <button key={i} className={`w-9 h-9 ${s.bg} ${s.color} rounded-xl flex items-center justify-center border border-slate-100 hover:scale-105 transition-transform`}>
                    <s.icon className="w-4 h-4" />
                  </button>
                ))}
              </div>

              {/* Views + Likes */}
              <div className="flex items-center justify-center gap-4 pt-2">
                <span className="text-[10px] text-slate-400 flex items-center gap-1"><Eye className="w-3 h-3" /> 1.842 views hoje</span>
                <span className="text-[10px] text-slate-400 flex items-center gap-1"><Heart className="w-3 h-3 text-rose-400" /> 234 favoritos</span>
              </div>
            </div>

            {/* ── TABS ── */}
            <div className="flex border-b border-slate-100">
              {[
                { id: 'links', label: 'Links & Ações' },
                { id: 'loja',  label: 'Mini-Loja' },
                { id: 'agenda', label: 'Horários' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex-1 py-2.5 text-[10px] font-bold border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-[#0061FF] text-[#0061FF]'
                      : 'border-transparent text-slate-400 hover:text-slate-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ── TAB CONTENT ── */}
            <div className="p-4 space-y-2.5">

              {activeTab === 'links' && (
                <>
                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
                    className="w-full bg-[#0061FF] text-white font-bold text-xs py-3.5 px-4 rounded-2xl flex items-center justify-between transition-colors hover:bg-blue-700">
                    <div className="flex items-center gap-2.5">
                      <MessageSquare className="w-4 h-4" />
                      <span>Fazer Pedido via WhatsApp</span>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                  </a>

                  {[
                    { icon: ShoppingBag, label: 'Cardápio PWA sem Taxas', badge: '0% taxa', bc: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
                    { icon: MapPin,      label: 'Ver no Google Maps',      badge: '⭐ 4.9',  bc: 'text-amber-700 bg-amber-50 border-amber-200' },
                    { icon: Calendar,   label: 'Agendar via Calendly',    badge: 'Online', bc: 'text-blue-700 bg-blue-50 border-blue-200' },
                    { icon: Instagram,  label: 'Siga no Instagram',       badge: '12.4k',  bc: 'text-pink-700 bg-pink-50 border-pink-200' },
                  ].map((link, i) => (
                    <button key={i} className="w-full bg-white border border-slate-200/80 text-slate-900 font-bold text-xs py-3 px-3.5 rounded-2xl flex items-center justify-between hover:border-slate-300 transition-colors">
                      <div className="flex items-center gap-2.5">
                        <link.icon className="w-4 h-4 text-slate-500" />
                        <span>{link.label}</span>
                      </div>
                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded-lg border ${link.bc}`}>{link.badge}</span>
                    </button>
                  ))}

                  <div className="grid grid-cols-3 gap-2 pt-1">
                    {[
                      { label: 'Cliques hoje', value: '342' },
                      { label: 'Pedidos',      value: '28' },
                      { label: 'Conversão',    value: '8.2%' },
                    ].map((s, i) => (
                      <div key={i} className="bg-slate-50 border border-slate-100 rounded-xl p-2.5 text-center">
                        <strong className="text-sm font-black text-slate-900 block">{s.value}</strong>
                        <span className="text-[9px] text-slate-400 font-medium">{s.label}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {activeTab === 'loja' && (
                <div className="space-y-2">
                  <p className="text-[10px] text-slate-500 font-medium">🔥 Mais pedidos da semana</p>
                  {CATALOG.map((item, i) => (
                    <div key={i} className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden flex gap-0">
                      <div className="w-20 h-20 shrink-0 overflow-hidden">
                        <img src={item.img} className="w-full h-full object-cover" alt={item.name} />
                      </div>
                      <div className="flex-1 p-3 flex flex-col justify-between">
                        <div>
                          <span className="text-[9px] font-bold text-slate-400 block">{item.tag}</span>
                          <strong className="text-[11px] font-bold text-slate-900 block leading-tight">{item.name}</strong>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <strong className="text-sm font-black text-slate-900">{item.price}</strong>
                          <button className="bg-[#0061FF] text-white text-[10px] font-bold px-3 py-1.5 rounded-xl">Pedir</button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button className="w-full text-[10px] font-bold text-[#0061FF] py-2 border border-dashed border-blue-200 rounded-xl hover:bg-blue-50 transition-colors">
                    Ver cardápio completo →
                  </button>
                </div>
              )}

              {activeTab === 'agenda' && (
                <div className="space-y-2">
                  {SCHEDULE.map((s, i) => (
                    <div key={i} className="bg-white border border-slate-200/80 rounded-xl px-3 py-2.5 flex items-center justify-between">
                      <div>
                        <strong className="text-[11px] font-bold text-slate-900">{s.day}</strong>
                        <span className="text-[10px] text-slate-500 block">{s.time}</span>
                      </div>
                      <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">Aberto</span>
                    </div>
                  ))}
                  <div className="bg-[#EFF6FF] border border-blue-100 rounded-xl px-3 py-3 mt-2">
                    <strong className="text-[11px] font-bold text-slate-900 block">Agendar Visita ou Evento</strong>
                    <p className="text-[9px] text-slate-500 mt-0.5">Reserve com antecedência via Calendly.</p>
                    <button className="mt-2 bg-[#0061FF] text-white text-[10px] font-bold px-3 py-1.5 rounded-lg">Agendar Agora</button>
                  </div>
                </div>
              )}

            </div>

            {/* Footer */}
            <div className="border-t border-slate-100 px-4 py-3 text-center">
              <span className="text-[9px] text-slate-400 font-medium">
                Criado com <strong className="text-slate-700 font-black">Linkwi 2.0</strong> · YouMachine Genesis 360°
              </span>
            </div>

          </div>
        </div>
      </div>

    </div>
  );
};
